import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from './store';
import { useFeatures } from '@/shared/config/useFeatures';
import { renewSession } from './renewApi';
import { playWarningChime } from './chime';
import { registerKioskRecovery } from './on401';

/**
 * The client half of PH10's idle session — slice-3560 (ADR-0046).
 *
 * <p>Mounted once by the app shell (never on `/login`). It does four things:
 *
 * <ol>
 *   <li><b>Tracks activity.</b> pointer / keyboard / scroll, throttled to one ref write per
 *       second — PB re-arms its idle timer from every ancestor control's `getfocus`
 *       (`f_phfc_handle_sys_in_activity.srf`, 18 call sites); the browser gives us the same
 *       coverage from four listeners on `window`.</li>
 *   <li><b>Renews proactively</b> while the user is working, so an actively-typing operator is
 *       never signed out — the thing testers actually complained about under the old
 *       60-minute absolute token.</li>
 *   <li><b>Warns</b> at `IdleMinutes − WarnSeconds` of inactivity with a live countdown and a
 *       chime, PB's `w_phfc_timer_countdown` in a browser.</li>
 *   <li><b>Signs out with a reason</b> on expiry, instead of the silent clear-and-redirect
 *       that used to lose the operator's place and their typing.</li>
 * </ol>
 *
 * <h4>Why the cadence is `min(tokenTtl/2, idleMinutes/2)` and not just the token half-life</h4>
 * The server's idle gate reads the session's `lastSeenAt` as it was <i>before</i> this request
 * touched it (`TenantFilter.PREVIOUS_LAST_SEEN_ATTR`). A tenant with a short
 * `Session.IdleMinutes` and no other API traffic between renewals would therefore trip its own
 * idle gate on the renew call itself if we only renewed at the token's half-life. Halving the
 * idle window too keeps every renewal comfortably inside it.
 *
 * <h4>Why there is a second, keep-alive reason to renew</h4>
 * Renewing ONLY on activity would make the tenant's `Session.IdleMinutes` decorative: with the
 * shipped defaults (60-minute idle limit, 20-minute token) an operator who steps away stops
 * generating activity, nothing renews, and the token dies at ~20 minutes — the warning modal
 * would never fire at all. So the hook also tops the token up when it would otherwise expire
 * while the session is still inside its idle window, and stops the moment the modal opens.
 *
 * <h4>Kiosk</h4>
 * A kiosk session renews on the timer with no activity gate, never opens the modal, and — via
 * `registerKioskRecovery` — answers a transient 401 with a renew attempt instead of a redirect.
 * The read-only guarantee is not here: it is `KioskWriteBlockFilter` on the server. This hook
 * only decides whether a break-room TV gets dumped on a login form.
 */

/** Throttle for activity bookkeeping. Sub-second precision buys nothing on a minutes-scale gate. */
const ACTIVITY_THROTTLE_MS = 1_000;

/** Floor on the renewal cadence, so a pathological config can't turn this into a request loop. */
const MIN_RENEW_INTERVAL_MS = 30_000;

/** Seconds remaining at which the second (and last) chime plays. */
const SECOND_CHIME_AT_SECONDS = 10;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'wheel'] as const;

export const IDLE_TIMEOUT_MESSAGE =
  'You were signed out after a period of inactivity. Sign in again to pick up where you left off.';
export const SESSION_MAX_AGE_MESSAGE =
  'Your session reached its maximum length and was signed out. Sign in again to continue.';
export const SESSION_ENDED_MESSAGE =
  'Your session could not be extended. Sign in again to pick up where you left off.';

export interface SessionExpiryState {
  /** True while the idle-warning modal should be on screen. Never true for a kiosk session. */
  warning: boolean;
  /** Whole seconds until sign-out, for the modal's `M:SS` countdown. */
  secondsLeft: number;
  /** Minutes of inactivity the warning is reporting — PB's "WS has been idle for N minutes". */
  idleMinutes: number;
  /** A renew request is in flight (disables "Keep working" so it can't be double-fired). */
  renewing: boolean;
  /** PB's Resume: count this as activity, renew, and close the modal on success. */
  keepWorking: () => void;
  /** Leave now rather than waiting out the countdown. */
  signOutNow: () => void;
}

function parseInstant(iso: string | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

export function useSessionExpiry(): SessionExpiryState {
  const session = useAuthStore((s) => s.session);
  const features = useFeatures();
  const cfg = features.session;

  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [renewing, setRenewing] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const activitySinceRenewRef = useRef(false);
  const lastRenewRef = useRef(Date.now());
  const renewInFlightRef = useRef(false);
  /** Which chimes this warning episode has already played. Reset when the modal closes. */
  const chimedRef = useRef({ open: false, ten: false });

  const signedIn = session != null;
  const kiosk = session?.kiosk === true;
  const idleMinutes = cfg.idleMinutes;
  const warnSeconds = cfg.warnSeconds;
  const warnSound = cfg.warnSound;

  const idleLimitMs = idleMinutes * 60_000;
  const warnAfterMs = Math.max(0, idleLimitMs - warnSeconds * 1_000);
  // See the javadoc: half the TOKEN life AND half the IDLE window, whichever is sooner.
  const renewIntervalMs = Math.max(
    MIN_RENEW_INTERVAL_MS,
    Math.min((cfg.tokenTtlMinutes * 60_000) / 2, idleLimitMs / 2),
  );

  const expire = useCallback((message: string) => {
    setWarning(false);
    chimedRef.current = { open: false, ten: false };
    useAuthStore.getState().clear(message);
  }, []);

  /**
   * One renewal attempt. Serialised through a ref — the timer, "Keep working" and the kiosk
   * 401 recovery can all ask at once, and three concurrent renews would each mint a token,
   * with the last write winning arbitrarily.
   */
  const renew = useCallback(
    async (opts?: { expireOnFailure?: boolean }): Promise<boolean> => {
      if (renewInFlightRef.current) return false;
      if (!useAuthStore.getState().session) return false;
      renewInFlightRef.current = true;
      setRenewing(true);
      try {
        const r = await renewSession();
        useAuthStore.getState().applyRenewal(r);
        lastRenewRef.current = Date.now();
        activitySinceRenewRef.current = false;
        return true;
      } catch {
        // The server refuses a renew for exactly the reasons that end a session (expired
        // token, dead session, past the idle limit or the absolute cap). Whether that is
        // fatal depends on who asked: a background top-up can wait for the next tick, but a
        // user who just clicked "Keep working" must be told.
        if (opts?.expireOnFailure) expire(SESSION_ENDED_MESSAGE);
        // Back off so a hard-down backend isn't hammered once per tick.
        lastRenewRef.current = Date.now();
        return false;
      } finally {
        renewInFlightRef.current = false;
        setRenewing(false);
      }
    },
    [expire],
  );

  // ── activity tracking ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!signedIn) return;
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last < ACTIVITY_THROTTLE_MS) return;
      last = now;
      lastActivityRef.current = now;
      activitySinceRenewRef.current = true;
    };
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true, capture: true }),
    );
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity, { capture: true }));
    };
  }, [signedIn]);

  // ── a session persisted by a pre-slice-3560 build has no expiresAt: fill it in once ──
  useEffect(() => {
    if (!signedIn) return;
    if (session?.expiresAt) return;
    void renew();
  }, [signedIn, session?.expiresAt, renew]);

  // ── kiosk: answer a transient 401 with a renew, never a redirect ─────────────────────
  useEffect(() => {
    if (!signedIn || !kiosk) return;
    return registerKioskRecovery(() => {
      void renew();
    });
  }, [signedIn, kiosk, renew]);

  // ── reset the clocks whenever a NEW session starts ───────────────────────────────────
  const sessionId = session?.sessionId;
  useEffect(() => {
    if (!sessionId) return;
    const now = Date.now();
    lastActivityRef.current = now;
    lastRenewRef.current = now;
    activitySinceRenewRef.current = false;
    chimedRef.current = { open: false, ten: false };
    setWarning(false);
  }, [sessionId]);

  // ── the one-second tick ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!signedIn) {
      setWarning(false);
      return;
    }

    const tick = () => {
      const store = useAuthStore.getState().session;
      if (!store) return;
      const now = Date.now();

      // 1. The absolute cap. No activity moves it and no renewal survives it, so say so
      //    rather than letting the next request come back 401 out of nowhere.
      const sessionEnd = parseInstant(store.sessionExpiresAt);
      if (sessionEnd !== null && now >= sessionEnd) {
        expire(SESSION_MAX_AGE_MESSAGE);
        return;
      }

      // 2. The token itself. An expired token can never be renewed (that is the whole point
      //    of refusing expired tokens server-side), so this is terminal too.
      const tokenEnd = parseInstant(store.expiresAt);
      if (tokenEnd !== null && now >= tokenEnd) {
        expire(kiosk ? SESSION_ENDED_MESSAGE : IDLE_TIMEOUT_MESSAGE);
        return;
      }

      // 3. Kiosk: renew on the timer, no activity gate, no modal, ever.
      if (kiosk) {
        if (now - lastRenewRef.current >= renewIntervalMs) void renew();
        return;
      }

      const idleFor = now - lastActivityRef.current;

      if (idleFor >= idleLimitMs) {
        expire(IDLE_TIMEOUT_MESSAGE);
        return;
      }

      if (idleFor >= warnAfterMs) {
        const remaining = Math.max(0, Math.ceil((idleLimitMs - idleFor) / 1000));
        setWarning(true);
        setSecondsLeft((prev) => (prev === remaining ? prev : remaining));
        if (!chimedRef.current.open) {
          chimedRef.current.open = true;
          if (warnSound) playWarningChime();
        } else if (!chimedRef.current.ten && remaining <= SECOND_CHIME_AT_SECONDS) {
          chimedRef.current.ten = true;
          if (warnSound) playWarningChime();
        }
        return;
      }

      // 4. Below the warning threshold: close any open modal and top the token up.
      if (chimedRef.current.open || chimedRef.current.ten) {
        chimedRef.current = { open: false, ten: false };
      }
      setWarning((w) => (w ? false : w));

      // Two reasons to renew, and BOTH are needed.
      //
      // (a) The user is working. Renew on the cadence so an active operator is never
      //     interrupted — the actual complaint under the old absolute token.
      //
      // (b) The token is about to die while the session is still inside its idle window.
      //     Without this the tenant's `Session.IdleMinutes` would be decorative: with the
      //     defaults (60-minute idle limit, 20-minute token) an operator who steps away
      //     would lose the token at ~20 minutes and never see the warning modal at all.
      //     The lead includes the whole warning window because renewals STOP once the modal
      //     opens — from there the countdown is the only thing that may end the session, and
      //     "Keep working" is the only thing that may extend it.
      const dueByActivity =
        activitySinceRenewRef.current && now - lastRenewRef.current >= renewIntervalMs;
      const dueBeforeTokenDies =
        tokenEnd !== null &&
        tokenEnd - now <= renewIntervalMs + warnSeconds * 1_000 &&
        now - lastRenewRef.current >= MIN_RENEW_INTERVAL_MS;
      if (dueByActivity || dueBeforeTokenDies) {
        void renew();
      }
    };

    const id = window.setInterval(tick, 1_000);
    return () => window.clearInterval(id);
  }, [
    signedIn,
    kiosk,
    idleLimitMs,
    warnAfterMs,
    warnSeconds,
    renewIntervalMs,
    warnSound,
    renew,
    expire,
  ]);

  const keepWorking = useCallback(() => {
    lastActivityRef.current = Date.now();
    activitySinceRenewRef.current = true;
    chimedRef.current = { open: false, ten: false };
    void renew({ expireOnFailure: true }).then((ok) => {
      if (ok) setWarning(false);
    });
  }, [renew]);

  const signOutNow = useCallback(() => {
    setWarning(false);
    chimedRef.current = { open: false, ten: false };
    useAuthStore.getState().clear();
  }, []);

  return { warning, secondsLeft, idleMinutes, renewing, keepWorking, signOutNow };
}
